import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import Langs from "../../app/lang/langs";
import styles from "./VResume.module.css";
import VResumeList from "./VResumeList";
import VResumeEditor from "./VResumeEditor";
import MobileVResumeView from "./mobile/MobileVResumeView";
import { getVResume, getVResumePostes, openEditor } from "./vresumeSlice";

/**
 * V-Resume — the semi-automatic CV builder.
 *
 * The switchboard: the team listing, or the editor for one CV. Which one is showing is
 * `state.vresume.editingCollabId` rather than a route param, so opening a CV and coming back
 * does not lose the listing's search text and filter — a manager working through a team
 * opens several CVs in a row.
 *
 * ── Every collab lands here, and that is the design ───────────────────────────────────
 *
 * `Body.js` mounts this view with no permission check, like V-Sécurité and V-Reference: the
 * floor is "every collaborator owns and edits their own CV", so there is nothing at the view
 * level to refuse. The `vresume` scope does not grant entry, it only widens which OTHER
 * people's CVs the user reaches — which is a per-row decision, made by the server.
 *
 * A collab with no reach still gets the listing (holding one row: theirs) plus the shortcut
 * button below, because that is a coherent screen rather than a special case to maintain.
 */
export default function VResumeView() {

    const dispatch = useDispatch()

    const editingCollabId = useSelector((state) => state.vresume.editingCollabId)
    const reach = useSelector((state) => state.vresume.reach)
    const isMobile = useSelector((state) => state.settings.isMobile)
    const userId = useSelector((state) => state.login.userId)

    // The postes referential feeds the headline picker. Fetched once for the whole view
    // rather than by the editor, so reopening a CV does not refetch a list that does not
    // change during a session.
    useEffect(() => {
        dispatch(getVResumePostes())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Loading the CV is driven by which collab is selected, not by the click, so a
    // deep-linked or restored `editingCollabId` loads the same way a clicked one does.
    useEffect(() => {
        if (!editingCollabId) return
        dispatch(getVResume(
            editingCollabId == userId ? {} : { id_collab: editingCollabId }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingCollabId])

    if (isMobile == 2 || isMobile == 3) return <MobileVResumeView />

    return (
        <div className={styles.bodyContainer}>
            <div className={`bodyCard ${styles.vresume}`}>

                {editingCollabId
                    ? <VResumeEditor />
                    : (
                        <div>
                            <div className={styles.pageHeader}>
                                <h2 className={styles.pageTitle}><Langs str="VRESUME_TITLE" /></h2>
                                <div className={styles.headerActions}>
                                    <Button size="small" variant="contained"
                                        startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => dispatch(openEditor(userId))}
                                        sx={{
                                            textTransform: 'none', fontFamily: 'roboto', fontSize: 12.5,
                                            backgroundColor: 'var(--orange)',
                                            '&:hover': { backgroundColor: '#e07f0d' }
                                        }}>
                                        <Langs str="VRESUME_OPEN_MINE" />
                                    </Button>
                                </div>
                            </div>

                            {/* Only shown to someone whose listing holds other people. A
                                collab seeing one row does not need it explained. */}
                            {reach.viewAll || reach.hasTeamReach
                                ? <p className={styles.sectionHint}>
                                    <Langs str="VRESUME_LIST_HINT" />
                                </p>
                                : null}

                            <VResumeList />
                        </div>
                    )}
            </div>
        </div>
    )
}
